/**
 * Shrinks a picture on the phone before it is sent (F24): every photo and document is at most 500 KB, and what is sent carries no camera data.
 * The picture is drawn again on a canvas, which also turns it the right way up and leaves the hidden location data behind. A PDF cannot be shrunk here,
 * so a PDF over the limit is refused with advice. The server checks the same limit and removes hidden data again, so an old app or a script cannot get around it.
 */
import { MAX_UPLOAD_BYTES } from '@konkride/shared';

/** Longest side in pixels and JPEG quality, best picture first. The first that fits the limit is used. */
export const SHRINK_STEPS: ReadonlyArray<readonly [number, number]> = [[2000, 0.85], [2000, 0.75], [1600, 0.8], [1600, 0.7], [1280, 0.75], [1280, 0.65], [1000, 0.7], [1000, 0.55]];
export type Encode = (longestSide: number, quality: number) => Promise<Blob | null>;

/** Never makes a picture bigger than it is. */
export const scaleFor = (w: number, h: number, longestSide: number): number => Math.min(1, longestSide / Math.max(w, h, 1));

export async function fitToLimit(encode: Encode, limit = MAX_UPLOAD_BYTES): Promise<Blob | null> {
  for (const [side, q] of SHRINK_STEPS) {
    const b = await encode(side, q);
    if (b && b.size <= limit) return b;
  }
  return null;
}

/** Opens a picture and returns a function that draws it at a chosen size and quality. Throws when the browser cannot open it. */
async function browserEncoder(file: File): Promise<Encode> {
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  return (side, q) => {
    const k = scaleFor(bmp.width, bmp.height, side), c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(bmp.width * k)); c.height = Math.max(1, Math.round(bmp.height * k));
    const g = c.getContext('2d');
    if (!g) return Promise.resolve(null);
    g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height); // a PNG with see-through parts must not turn black as a JPEG
    g.drawImage(bmp, 0, 0, c.width, c.height);
    return new Promise((done) => c.toBlob(done, 'image/jpeg', q));
  };
}

const kb = Math.round(MAX_UPLOAD_BYTES / 1024);
/** The file to send. Throws an Error whose message is for the person. */
export async function prepareUpload(file: File, limit = MAX_UPLOAD_BYTES, openPicture: (f: File) => Promise<Encode> = browserEncoder): Promise<File> {
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    if (file.size <= limit) return file;
    throw new Error(`This PDF is over ${kb} KB. Take a photo of the document instead.`);
  }
  let encode: Encode;
  try { encode = await openPicture(file); } catch { throw new Error('That picture could not be opened. Take it again, or pick a JPG or PNG.'); }
  const blob = await fitToLimit(encode, limit);
  if (!blob) throw new Error(`That picture is too detailed to fit ${kb} KB. Take it again, closer and with less around it.`);
  return new File([blob], file.name.replace(/\.[^.]*$/, '') + '.jpg', { type: 'image/jpeg' });
}
